import styled, { css } from 'styled-components';
import { CustomLink } from '@/components/CustomLink/CustomLink';
import { Typography } from '@/components/UI/Typography/Typography.styles';

const PLACEHOLDER = '/images/photo-placeholder.svg';

const RoundedBorders = css<{ $isFirst?: boolean; $isLast?: boolean }>`
  ${({ $isFirst }) =>
    $isFirst &&
    css`
      border-bottom-left-radius: 14px;
    `}

  ${({ $isLast }) =>
    $isLast &&
    css`
      border-bottom-right-radius: 14px;
    `}
`;

export const Card = styled(CustomLink)<{ $isInteractionBlocked: boolean }>`
  display: flex;
  flex-direction: column;
  min-width: 166px;
  position: relative;
  cursor: pointer;
  user-select: none;
  pointer-events: ${({ $isInteractionBlocked }) =>
    $isInteractionBlocked ? 'none' : 'auto'};

  @media (max-width: ${({ theme }) => theme.breakpoints.xsm}) {
    min-width: 130px;
  }
`;

export const CardPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  aspect-ratio: 93 / 100;
  overflow: hidden;
  gap: 10px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    aspect-ratio: 87 / 100;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.xsm}) {
    min-width: 130px;
  }
`;

export const Title = styled(Typography.Header).attrs(() => ({
  $variant: '3',
  $isEllipsis: true,
}))`
  line-height: 1.2;
  margin-top: 10px;
  text-overflow: ellipsis;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 13px;
  }
`;

export const Count = styled(Typography.Caption).attrs(() => ({
  $variant: '2',
}))`
  margin-top: 2px;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 12px;
  }
`;

export const CollectionEditButton = styled.button<{ $isActive?: boolean }>`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  border: none;
  padding: 0;
  width: 29px;
  height: 29px;
  border-radius: 50%;
  cursor: pointer;
  background-size: 15px;
  background-position: center;
  background-repeat: no-repeat;
  background-image: url('/images/dots.svg');
  transition: background-color 0.1s linear;
  background-color: ${({ theme, $isActive }) =>
    $isActive
      ? `${theme.palette.system.darkGray}90`
      : `${theme.palette.system.darkGray}60`};

  &:hover {
    background-color: ${({ theme }) => `${theme.palette.system.darkGray}90`};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 20px;
    height: 20px;
    background-size: 12px;
  }
`;

export const ImagesContainer = styled.div<{ $isPlaceholder?: boolean }>`
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: auto auto;
  gap: 2px;
  width: 100%;
  aspect-ratio: 305/283;
  user-select: none;
  display: ${({ $isPlaceholder }) => ($isPlaceholder ? 'flex' : 'grid')};
`;

export const MainImagePlaceholder = styled.div`
  width: 100%;
  object-fit: cover;
  border-radius: 14px;
  background-size: 44px;
  background-position: center;
  background-repeat: no-repeat;
  background-image: url(${PLACEHOLDER});
  background-color: ${({ theme }) => theme.palette.secondary.background};

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    background-size: 38px;
  }
`;

export const SmallImagePlaceholder = styled.div<{
  $isFirst?: boolean;
  $isLast?: boolean;
}>`
  width: 100%;
  aspect-ratio: 100/73;
  background-color: ${({ theme }) => theme.palette.secondary.background};
  ${RoundedBorders}
`;

export const MainImage = styled.img`
  grid-column: 1 / -1;
  width: 100%;
  aspect-ratio: 305/210;
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;
  object-fit: cover;
`;

export const SmallImage = styled.img<{ $isFirst?: boolean; $isLast?: boolean }>`
  width: 100%;
  aspect-ratio: 100/73;
  object-fit: cover;
  ${RoundedBorders}
`;
